import { Box, CircularProgress } from "@mui/material";
import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useV2Theme } from "../../hooks/useV2Theme";
import { AuthContext } from "../../../context/AuthContext";
import { useJobs } from "../../../hooks/useJobs";
import { useBulkExportDownload } from "../../../hooks/useBulkExport";
import { Job, JobState, JobType } from "../../../types/job/Job";
import { FONT_MONO } from "../../theme/v2Tokens";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeStatusBadge(t: (key: string) => string) {
  return function statusBadge(state: JobState) {
    const map: Record<JobState, { label: string; color: string; bg: string }> = {
      [JobState.InProgress]: {
        label: t("common:inProgress"),
        color: "var(--signal-blue)",
        bg: "rgba(56,189,248,0.12)",
      },
      [JobState.Complete]: {
        label: t("common:complete"),
        color: "var(--signal-emerald)",
        bg: "rgba(52,211,153,0.12)",
      },
      [JobState.Error]: {
        label: t("common:error"),
        color: "var(--signal-rose)",
        bg: "rgba(251,113,133,0.12)",
      },
    };
    const info = map[state] ?? {
      label: state,
      color: "var(--text-secondary)",
      bg: "transparent",
    };
    return info;
  };
}

function formatDate(iso: string | undefined | null): string {
  if (!iso) return "\u2014";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "\u2014";
  }
}

function parseDataExported(input: string | undefined): string {
  if (!input) return "\u2014";
  try {
    const parsed = JSON.parse(input);
    if (Array.isArray(parsed)) {
      // Could be array of strings or array of { value, label } objects
      return parsed
        .map((item: string | { value?: string; label?: string }) =>
          typeof item === "string" ? item : item.value ?? item.label ?? ""
        )
        .filter(Boolean)
        .join(", ");
    }
  } catch {
    // Not JSON — treat as plain string
  }
  return input;
}

function toolbarBtnSx(disabled: boolean) {
  return {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    px: "12px",
    py: "6px",
    borderRadius: "6px",
    border: "1px solid var(--border-mid)",
    bgcolor: "var(--bg-raised)",
    color: disabled ? "var(--text-tertiary)" : "var(--text-secondary)",
    fontSize: "0.8125rem",
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
    fontFamily: "inherit",
    opacity: disabled ? 0.5 : 1,
    "&:hover": disabled
      ? {}
      : { bgcolor: "var(--bg-hover)", color: "var(--text-primary)" },
  };
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function V2BulkExport() {
  const { t } = useTranslation(["common", "grid"]);
  const statusBadge = makeStatusBadge(t as (key: string) => string);
  const { customerId } = useParams<{ customerId: string }>();
  const { user } = useContext(AuthContext);
  useV2Theme();

  const hasBulkExport = user?.hasFeatureFlag("BulkExport") ?? false;

  const {
    data: jobs,
    isLoading,
    isFetching,
    refetch,
  } = useJobs(customerId, JobType.BulkExport);

  const downloadMutation = useBulkExportDownload();
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);

  const handleDownload = async (job: Job) => {
    if (!customerId) return;
    setDownloadingJobId(job.jobId);
    try {
      await downloadMutation.mutateAsync({
        customerId,
        jobId: job.jobId,
      });
    } catch (err) {
      console.error("Failed to download bulk export:", err);
    } finally {
      setDownloadingJobId(null);
    }
  };

  if (!hasBulkExport) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "var(--text-tertiary)",
          fontSize: "0.875rem",
          backgroundColor: "var(--bg-deep)",
        }}
      >
        {t("common:bulkExportNotEnabled")}
      </Box>
    );
  }

  const isDataLoading = isLoading || isFetching;
  const sortedJobs = [...(jobs ?? [])].sort(
    (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        backgroundColor: "var(--bg-deep)",
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          px: "12px",
          py: "8px",
          borderBottom: "1px solid var(--border-subtle)",
          backgroundColor: "var(--bg-surface)",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            fontSize: "0.875rem",
            fontWeight: 700,
            color: "var(--text-primary)",
            letterSpacing: "-0.01em",
          }}
        >
          {t("common:bulkExports")}
        </Box>

        <Box sx={{ flex: 1 }} />

        <Box
          component="button"
          onClick={() => refetch()}
          disabled={isDataLoading}
          sx={toolbarBtnSx(isDataLoading)}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: 13, height: 13 }}
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
          </svg>
          {t("common:refresh")}
        </Box>
      </Box>

      {/* Table area */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "auto" }}>
        {isDataLoading && sortedJobs.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "200px",
              gap: "12px",
              color: "var(--text-secondary)",
            }}
          >
            <CircularProgress size={20} sx={{ color: "var(--accent)" }} />
            <span>{t("common:loadingExportJobs")}</span>
          </Box>
        )}
        {!isDataLoading && sortedJobs.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "200px",
              color: "var(--text-tertiary)",
              fontSize: "0.875rem",
            }}
          >
            {t("common:noExportJobsFound")}
          </Box>
        )}
        {sortedJobs.length > 0 && (
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.8125rem",
              "& thead th": {
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "var(--bg-surface)",
                borderBottom: "1px solid var(--border-subtle)",
                px: "12px",
                py: "10px",
                textAlign: "left",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "var(--text-tertiary)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                whiteSpace: "nowrap",
              },
              "& tbody tr": {
                borderBottom: "1px solid var(--border-subtle)",
                transition: "background-color 100ms ease",
                "&:hover": {
                  backgroundColor: "var(--bg-hover)",
                },
              },
              "& td": {
                px: "12px",
                py: "10px",
                verticalAlign: "middle",
                whiteSpace: "nowrap",
                color: "var(--text-secondary)",
              },
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 60, textAlign: "center" }}>{t("common:download")}</th>
                <th>{t("common:status")}</th>
                <th>{t("common:startTime")}</th>
                <th>{t("common:endTime")}</th>
                <th>{t("common:records")}</th>
                <th>{t("common:dataExported")}</th>
                <th>{t("common:jobId")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedJobs.map((job) => {
                const badge = statusBadge(job.state);
                const isDownloading = downloadingJobId === job.jobId;
                const canDownload =
                  job.state === JobState.Complete && !isDownloading;

                const downloadTitle = canDownload
                  ? t("common:downloadExport")
                  : isDownloading
                    ? t("common:downloading")
                    : t("common:notAvailable");

                return (
                  <tr key={job.jobId}>
                    {/* Download */}
                    <Box
                      component="td"
                      sx={{ textAlign: "center", width: 60 }}
                    >
                      <Box
                        component="button"
                        onClick={() => handleDownload(job)}
                        disabled={!canDownload}
                        title={downloadTitle}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 30,
                          height: 30,
                          borderRadius: "6px",
                          border: "none",
                          background: "none",
                          color: canDownload
                            ? "var(--accent)"
                            : "var(--text-tertiary)",
                          cursor: canDownload ? "pointer" : "default",
                          opacity: canDownload ? 1 : 0.35,
                          transition:
                            "color 100ms ease, background-color 100ms ease",
                          "&:hover": canDownload
                            ? {
                                color: "var(--text-primary)",
                                backgroundColor: "var(--bg-raised)",
                              }
                            : {},
                        }}
                      >
                        {isDownloading ? (
                          <CircularProgress
                            size={16}
                            sx={{ color: "var(--accent)" }}
                          />
                        ) : (
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ width: 16, height: 16 }}
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                        )}
                      </Box>
                    </Box>

                    {/* Status */}
                    <td>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          px: "8px",
                          py: "3px",
                          borderRadius: "12px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: badge.color,
                          backgroundColor: badge.bg,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {badge.label}
                      </Box>
                    </td>

                    {/* Start Time */}
                    <td>{formatDate(job.startTime)}</td>

                    {/* End Time */}
                    <td>{formatDate(job.endTime)}</td>

                    {/* Records Processed */}
                    <Box
                      component="td"
                      sx={{
                        fontFamily: FONT_MONO,
                        fontSize: "0.8125rem",
                      }}
                    >
                      {job.recordsProcessed ?? "\u2014"}
                    </Box>

                    {/* Data Exported */}
                    <td>{parseDataExported(job.input)}</td>

                    {/* Job ID */}
                    <Box
                      component="td"
                      sx={{
                        fontFamily: FONT_MONO,
                        fontSize: "0.75rem",
                        color: "var(--text-tertiary) !important",
                      }}
                    >
                      {job.jobId}
                    </Box>
                  </tr>
                );
              })}
            </tbody>
          </Box>
        )}
      </Box>
    </Box>
  );
}
